import { computed, onMounted, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import Swal from "sweetalert2";

import registerService, { type PosRegister } from "@/services/register";
import userService, { type User } from "@/services/user";

type RegisterHistoryFilters = {
  user_id: number | null;
  status: number | null;
  from_date: string;
  to_date: string;
  per_page: number;
};

const createInitialFilters = (): RegisterHistoryFilters => ({
  user_id: null,
  status: null,
  from_date: "",
  to_date: "",
  per_page: 10,
});

export function useRegisterHistoryReport() {
  const { t } = useI18n();
  const items = ref<PosRegister[]>([]);
  const users = ref<User[]>([]);
  const loading = ref(false);
  const searchQuery = ref("");
  const currentPage = ref(1);
  const total = ref(0);
  const filters = reactive<RegisterHistoryFilters>(createInitialFilters());
  const i18nScope = "register_history_report_page";
  const pageTitle = `${i18nScope}.title`;

  const pagination = computed(() => ({
    current: currentPage.value,
    pageSize: filters.per_page,
    total: total.value,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "30", "50", "100"],
    showTotal: (total: number, range: [number, number]) =>
      t(`${i18nScope}.pagination_total`, { start: range[0], end: range[1], total }),
    locale: { items_per_page: t(`${i18nScope}.items_per_page`) },
  }));

  const summary = computed(() => ({
    openingBalance: items.value.reduce((sum, item) => sum + Number(item.opening_balance ?? 0), 0),
    closingBalance: items.value.reduce((sum, item) => sum + Number(item.closing_balance ?? 0), 0),
    difference: items.value.reduce((sum, item) => sum + Number(item.difference ?? 0), 0),
  }));

  const queryParams = computed(() => ({
    page: currentPage.value,
    per_page: filters.per_page,
    user_id: filters.user_id || undefined,
    status: filters.status ?? undefined,
    from_date: filters.from_date || undefined,
    to_date: filters.to_date || undefined,
    search_query: searchQuery.value || undefined,
  }));

  const fetchUsers = async () => {
    try {
      users.value = await userService.getUsers({ per_page: 1000 });
    } catch (error) {
      console.error("Error loading register history users:", error);
    }
  };

  const fetchHistory = async () => {
    loading.value = true;

    try {
      const response = await registerService.getHistory(queryParams.value);
      items.value = response.data ?? [];
      currentPage.value = response.current_page;
      total.value = response.total;
    } catch (error) {
      console.error("Error fetching register history:", error);
      Swal.fire({
        icon: "error",
        title: t(`${i18nScope}.load_failed`),
        text: t(`${i18nScope}.unable_to_load_report`),
      });
    } finally {
      loading.value = false;
    }
  };

  const handleTableChange = (page: number, pageSize: number) => {
    currentPage.value = page;

    if (pageSize !== filters.per_page) {
      filters.per_page = pageSize;
      currentPage.value = 1;
    }

    fetchHistory();
  };

  const applyFilters = () => {
    currentPage.value = 1;
    fetchHistory();
  };

  const resetFilters = () => {
    Object.assign(filters, createInitialFilters());
    currentPage.value = 1;
    fetchHistory();
  };

  watch(searchQuery, () => {
    currentPage.value = 1;
    fetchHistory();
  });

  onMounted(async () => {
    await fetchUsers();
    await fetchHistory();
  });

  return {
    pageTitle,
    i18nScope,
    items,
    users,
    loading,
    searchQuery,
    filters,
    pagination,
    summary,
    handleTableChange,
    applyFilters,
    resetFilters,
  };
}
